<template>
  <div class="qual-flow">
    <div class="qual-flow-container">

      <!-- ===== Top progress bar (clickable) ===== -->
      <div class="qual-stepper" :aria-current="stepIndex + 1">
        <div class="qual-stepper-line">
          <div class="qual-stepper-fill" :style="{ width: progressFillPercent + '%' }"></div>
        </div>
        <button
          type="button"
          v-for="(label, idx) in stepLabels"
          :key="label"
          class="qual-stepper-node"
          :class="{
            'is-done': idx < stepIndex,
            'is-current': idx === stepIndex,
            'is-clickable': canJumpToStep(idx),
            'is-rejected': rejectedStepIndices.includes(idx),
          }"
          :disabled="!canJumpToStep(idx)"
          @click="jumpToStep(idx)"
        >
          <div class="qual-stepper-dot">
            <b-icon v-if="idx < stepIndex" icon="check" />
            <span v-else>{{ idx + 1 }}</span>
          </div>
          <div class="qual-stepper-label">{{ label }}</div>
        </button>
      </div>

      <!-- ===== Loading ===== -->
      <div v-if="loading" class="qual-loading">
        <b-spinner /> <span class="ml-2">Загрузка…</span>
      </div>

      <!-- ===== Cool-down (3 fails / hour) ===== -->
      <div v-else-if="step === 'cooldown'" class="qual-card qual-card-warning">
        <b-icon icon="hourglass-split" class="qual-card-icon" />
        <h3 class="qual-card-title">Тест временно недоступен</h3>
        <p class="qual-card-text">{{ cooldownMessage }}</p>
        <p class="qual-card-hint">Следующая попытка через: <strong>{{ cooldownDisplay }}</strong></p>
      </div>

      <!-- ============================================================
           STEP 1 — Qualification: pick test OR upload certificate
      ============================================================ -->
      <div v-else-if="step === 'test'">
        <!-- Method picker -->
        <div v-if="!method" class="qual-method-grid">
          <!-- Once the test is passed it can't be retaken — render the
               card as a passive "already done" tile instead of a CTA. -->
          <button type="button" class="qual-method-card"
                  :class="{ 'is-done': qualifiedPassed }"
                  :disabled="qualifiedPassed"
                  @click="qualifiedPassed ? null : pickMethod('test')">
            <b-icon :icon="qualifiedPassed ? 'check-circle-fill' : 'clipboard-check'" class="qual-method-icon" />
            <div class="qual-method-title">
              {{ qualifiedPassed ? 'Тест уже пройден' : 'Пройти тест' }}
            </div>
            <div class="qual-method-sub">
              <template v-if="qualifiedPassed">
                Повторное прохождение не требуется.
              </template>
              <template v-else>
                10 вопросов. Минимум 8 верных. После 3 неудач подряд — пауза 60 мин.
              </template>
            </div>
          </button>
          <button type="button" class="qual-method-card" @click="pickMethod('cert')">
            <b-icon icon="patch-check" class="qual-method-icon" />
            <div class="qual-method-title">У меня есть сертификат</div>
            <div class="qual-method-sub">
              CFA / FRM / CAIA / CIIA — прохождение теста не требуется.
            </div>
          </button>
        </div>

        <!-- Cert upload (path=cert) -->
        <div v-else-if="method === 'cert'" class="qual-card">
          <h3 class="qual-card-title">Профессиональный сертификат</h3>
          <p class="qual-card-text">
            Загрузите скан/копию действующего сертификата (CFA, FRM, CAIA,
            CIIA). Можно прикрепить несколько файлов сразу — например,
            оригинал и перевод.
          </p>

          <div
            class="qual-upload-zone"
            :class="{ 'is-dragover': certDragOver, 'has-file': certFiles.length }"
            @dragover.prevent="certDragOver = true"
            @dragleave.prevent="certDragOver = false"
            @drop.prevent="onCertDrop"
            @click="$refs.certInput && $refs.certInput.click()"
          >
            <input
              ref="certInput"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.zip,.rar"
              class="qual-upload-input"
              @change="onCertFileSelect"
            />
            <b-icon icon="file-earmark-arrow-up" class="qual-upload-icon" />
            <p class="qual-upload-text">
              Перетащите файлы сюда или нажмите для выбора
            </p>
            <p class="qual-upload-hint">PDF, JPG, PNG, ZIP или RAR. Не более 10 МБ за файл.</p>
          </div>

          <ul v-if="certFiles.length" class="qual-upload-files">
            <li v-for="(f, i) in certFiles" :key="i" class="qual-upload-files-item">
              <b-icon icon="file-earmark" class="mr-2" />
              <span class="qual-upload-files-name">{{ f.name }}</span>
              <button type="button" class="qual-upload-files-remove"
                      @click.stop="removeCertFile(i)" title="Убрать файл">
                <b-icon icon="x" />
              </button>
            </li>
          </ul>

          <p v-if="certError" class="qual-error">
            <b-icon icon="exclamation-circle" class="mr-1" />{{ certError }}
          </p>

          <!-- Previously submitted certificates with their MLRO status.
               Same shape as the SoF list but filtered to CERTIFICATE. -->
          <div v-if="certDocs.length || docListLoading" class="qual-doc-list">
            <div class="qual-doc-list-head">
              <h6 class="qual-doc-list-title">Ранее загруженный сертификат</h6>
              <b-button size="sm" variant="outline-light" :disabled="docListLoading"
                        @click="fetchCertDocs" title="Обновить">
                <b-icon icon="arrow-repeat" :animation="docListLoading ? 'spin' : null" />
              </b-button>
            </div>
            <div v-if="docListLoading" class="qual-doc-list-empty">
              <b-spinner small /> <span class="ml-2">Загрузка…</span>
            </div>
            <table v-else class="qual-doc-list-table">
              <thead>
                <tr>
                  <th>Файл</th>
                  <th>Статус</th>
                  <th>Комментарий</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(doc, idx) in certDocs" :key="doc.docId != null ? doc.docId : idx">
                  <td><div class="qual-doc-type">{{ doc.docType || 'CERTIFICATE' }}</div></td>
                  <td>
                    <span :class="['qual-doc-status', 'qual-doc-status-' + (doc.docStatus || '').toLowerCase()]">
                      {{ doc.docStatus || '—' }}
                    </span>
                  </td>
                  <td class="qual-doc-comment">{{ doc.mlroDesc || '—' }}</td>
                  <td>
                    <b-button size="sm" variant="outline-light" @click="downloadDoc(doc)">
                      <b-icon icon="download" />
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p v-if="certUploadError" class="qual-error">
            <b-icon icon="exclamation-circle" class="mr-1" />{{ certUploadError }}
          </p>

          <div class="qual-actions">
            <b-button variant="link" class="text-muted" @click="resetMethod">
              ← Выбрать другой способ
            </b-button>
            <!-- One-button flow: if a new cert is picked, save it via
                 /ptest/postcert and move on; if no new file but the user
                 already has a cert on file, just advance. The wizard
                 stepper lets them come back here to add another cert
                 later. -->
            <b-button
              variant="warning"
              :disabled="(!certFiles.length && !certDocs.length) || certUploading"
              @click="advanceFromCert"
            >
              <b-spinner v-if="certUploading" small class="mr-2" />
              Далее
            </b-button>
          </div>
        </div>

        <!-- Test — questions -->
        <div v-else-if="testPhase === 'questions'" class="qual-card">
          <p class="qual-progress-text">
            Вопрос {{ currentQuestionIndex + 1 }} из {{ questions.length }}
            (минимум 8 верных для прохождения)
          </p>

          <div v-if="currentQuestion" class="qual-question-block">
            <p class="qual-question-text">{{ (currentQuestion.questionRu || '').trim() }}</p>
            <div class="qual-answers">
              <label
                v-for="a in currentQuestion.answers"
                :key="a.answerId"
                class="qual-answer-label"
                :class="{ checked: isAnswerSelected(currentQuestion.questionId, a.answerId) }"
              >
                <input
                  type="radio"
                  :name="'q' + currentQuestion.questionId"
                  :value="a.answerId"
                  :checked="isAnswerSelected(currentQuestion.questionId, a.answerId)"
                  @change="selectAnswer(currentQuestion.questionId, a)"
                />
                <span class="qual-answer-text">{{ (a.answerRu || '').trim() }}</span>
              </label>
            </div>
          </div>

          <div class="qual-actions">
            <b-button
              variant="outline-light"
              :disabled="currentQuestionIndex === 0"
              @click="prevQuestion"
            >
              Назад
            </b-button>
            <b-button
              v-if="!isLastQuestion"
              variant="primary"
              :disabled="!canGoNext"
              @click="nextQuestion"
            >
              Далее
            </b-button>
            <b-button
              v-else
              variant="warning"
              :disabled="!canGoNext || submitting"
              @click="submitTest"
            >
              <b-spinner v-if="submitting" small class="mr-2" />
              Завершить тест
            </b-button>
          </div>
        </div>

        <!-- b) Failed result + retry -->
        <div v-else-if="testPhase === 'failed'" class="qual-card qual-card-warning">
          <b-icon icon="exclamation-triangle" class="qual-card-icon" />
          <h3 class="qual-card-title">Тест не пройден</h3>
          <p class="qual-card-text">
            Для успешного прохождения теста необходимо ответить корректно не менее чем на 8 из 10 поставленных вопросов.
          </p>
          <p v-if="attemptsLeft !== null && attemptsLeft < 3" class="qual-card-hint">
            Оставшиеся попытки: <strong>{{ attemptsLeft }} из 3</strong>.
            В случае трех последовательных неудачных попыток, система будет заблокирована на 60 минут, предоставив Вам время на дополнительную подготовку.
          </p>
          <div class="qual-actions">
            <b-button variant="warning" @click="restartTest">
              <b-icon icon="arrow-repeat" class="mr-2" />Пройти повторно
            </b-button>
          </div>
        </div>
      </div>

      <!-- ============================================================
           STEP 2 — SoF upload
      ============================================================ -->
      <div v-else-if="step === 'sof'" class="qual-card">
        <h3 class="qual-card-title">Подтверждение источника средств (SoF)</h3>
        <p class="qual-card-text">
          Загрузите документы, подтверждающие происхождение средств: справки о
          доходах, банковские выписки, инвестиционные отчёты, документы о
          продаже активов, наследстве и т. п. Поддерживаются PDF, JPG, PNG,
          а также архивы ZIP / RAR.
        </p>

        <div
          class="qual-upload-zone"
          :class="{
            'is-dragover': sofDragOver,
            'has-file': sofFile,
          }"
          @dragover.prevent="sofDragOver = true"
          @dragleave.prevent="sofDragOver = false"
          @drop.prevent="onSofDrop"
          @click="!sofFile && $refs.sofInput && $refs.sofInput.click()"
        >
          <input
            ref="sofInput"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.zip,.rar"
            class="qual-upload-input"
            @change="onSofFileSelect"
          />
          <template v-if="!sofFile">
            <b-icon icon="cloud-arrow-up" class="qual-upload-icon" />
            <p class="qual-upload-text">Перетащите файл сюда или нажмите для выбора</p>
            <p class="qual-upload-hint">PDF, JPG, PNG, ZIP или RAR. Не более 10 МБ.</p>
          </template>
          <template v-else>
            <b-icon icon="file-earmark-zip" class="qual-upload-icon" />
            <p class="qual-upload-filename">{{ sofFile.name }}</p>
            <button type="button" class="qual-upload-clear" @click.stop="clearSofFile">
              Удалить и выбрать другой
            </button>
          </template>
        </div>

        <p v-if="sofError" class="qual-error">
          <b-icon icon="exclamation-circle" class="mr-1" />{{ sofError }}
        </p>

        <!-- Already uploaded documents for this user (any status). Shows
             cert + SoF history so the user understands what's already
             pending MLRO review and can re-download. -->
        <div class="qual-doc-list">
          <div class="qual-doc-list-head">
            <h6 class="qual-doc-list-title">Загруженные документы</h6>
            <b-button
              size="sm"
              variant="outline-light"
              :disabled="docListLoading"
              @click="fetchSofDocs"
              title="Обновить список"
            >
              <b-icon icon="arrow-repeat" :animation="docListLoading ? 'spin' : null" />
            </b-button>
          </div>
          <div v-if="docListLoading" class="qual-doc-list-empty">
            <b-spinner small /> <span class="ml-2">Загрузка…</span>
          </div>
          <div v-else-if="!sofDocs || sofDocs.length === 0" class="qual-doc-list-empty">
            Пока ничего не загружено.
          </div>
          <table v-else class="qual-doc-list-table">
            <thead>
              <tr>
                <th>Тип / файл</th>
                <th>Статус</th>
                <th>Комментарий</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(doc, idx) in sofDocs" :key="doc.docId != null ? doc.docId : idx">
                <td>
                  <div class="qual-doc-type">{{ doc.docType || '—' }}</div>
                </td>
                <td>
                  <span :class="['qual-doc-status', 'qual-doc-status-' + (doc.docStatus || '').toLowerCase()]">
                    {{ doc.docStatus || '—' }}
                  </span>
                </td>
                <td class="qual-doc-comment">{{ doc.mlroDesc || '—' }}</td>
                <td>
                  <b-button size="sm" variant="outline-light" @click="downloadDoc(doc)">
                    <b-icon icon="download" />
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="qual-actions">
          <b-button variant="outline-light" @click="leaveForLater">
            Позже
          </b-button>
          <b-button
            variant="warning"
            :disabled="!sofFile || uploading"
            @click="uploadSof"
          >
            <b-spinner v-if="uploading" small class="mr-2" />
            Отправить на проверку
          </b-button>
        </div>
      </div>

      <!-- ============================================================
           STEP 3 — Done
      ============================================================ -->
      <div v-else-if="step === 'done'" class="qual-card" :class="statusCardClass">
        <b-icon :icon="statusIcon" class="qual-card-icon" />
        <h3 class="qual-card-title">{{ statusTitle }}</h3>
        <p class="qual-card-text">{{ statusText }}</p>

        <!-- Rejected files list — one block per refused document with
             the MLRO comment so the client knows what to fix and
             re-upload. -->
        <div v-if="rejectedDocs.length" class="qual-rejected-list">
          <div
            v-for="d in rejectedDocs"
            :key="'rej-' + d.docId"
            class="qual-rejected-row"
          >
            <div class="qual-rejected-head">
              <span class="qual-rejected-name">{{ docTypeLabel(d.docType) }}</span>
              <span class="qual-rejected-badge">Отклонён</span>
            </div>
            <div v-if="d.mlroDesc" class="qual-rejected-reason">
              <strong>Причина:</strong> {{ d.mlroDesc }}
            </div>
          </div>
        </div>

        <div class="qual-actions">
          <b-button
            v-if="canResubmit"
            variant="danger"
            pill
            @click="resubmitApplication"
          >
            Подать заявку заново
          </b-button>
          <b-button variant="outline-light" pill @click="$router.push('/landing')">
            Вернуться на главную
          </b-button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import { apiDomain, getHeader } from '@/config/config'
import { EventBus } from '@/EventBus'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

export default {
  name: 'QualifiedFlow',
  data() {
    return {
      loading: true,
      // 'test' | 'sof' | 'done' | 'cooldown'
      step: 'test',
      // Path through qualify step: 'test' (pass exam) | 'cert' (upload
      // CFA/FRM/CAIA/CIIA certificate). Null means the user hasn't
      // picked yet and we render the method picker.
      method: null,
      // sub-phase of 'test' step (path=test only): 'questions' | 'failed'
      testPhase: 'questions',
      // Mirror of lms_core_user.is_passed_qualified — drives the "test
      // already taken" lock on the method picker.
      qualifiedPassed: false,

      // Test state
      testData: null,
      currentQuestionIndex: 0,
      selectedAnswers: {},
      submitting: false,
      attemptsLeft: null,
      cooldownMessage: '',
      cooldownSeconds: 0,
      cooldownTimer: null,

      // Certificate state (path=cert). certFiles is multi — users can
      // attach the certificate scan plus translation/extra pages in one go.
      certDragOver: false,
      certFiles: [],
      certError: null,
      // Extra-upload submit state (cert-only, /ptest/postcert)
      certUploading: false,
      certUploadError: null,

      // SoF state
      sofDragOver: false,
      sofFile: null,
      uploading: false,
      sofError: null,

      // Previously uploaded docs. Two separate lists, one per docType,
      // so each step only fetches what it needs to display.
      sofDocs: [],
      certDocs: [],
      docListLoading: false,
      // Latest RETAIL_Q classification request — drives the status
      // screen (step='done'). Shape: { status, requestedCategory,
      // attemptId, documents:[{docType, docStatus, mlroDesc, ...}] }.
      request: null,
    }
  },
  watch: {
    step(next) {
      if (next === 'sof') this.fetchSofDocs()
    },
  },
  computed: {
    ...mapState(['loginedUser']),
    // ---- Status screen helpers (step='done') ----
    requestStatus() {
      return (this.request && this.request.status) || ''
    },
    rejectedDocs() {
      // Per docType take only the latest upload — older REJECTED rows
      // are obsolete once the user has re-uploaded a replacement.
      const docs = (this.request && this.request.documents) || []
      const latest = new Map()
      docs.forEach(d => {
        if (!d || !d.docType) return
        const prev = latest.get(d.docType)
        if (!prev || (d.docId || 0) > (prev.docId || 0)) latest.set(d.docType, d)
      })
      return [...latest.values()].filter(d => d.docStatus === 'REJECTED')
    },
    // Stepper indices that should render in red so the client sees at
    // a glance which stage has a refused document. QUALIFIED is the
    // SoF step (idx 1); CERTIFICATE is the qualification step (idx 0).
    rejectedStepIndices() {
      if (this.requestStatus !== 'REJECTED') return []
      const out = []
      const types = this.rejectedDocs.map(d => d.docType)
      if (types.includes('CERTIFICATE')) out.push(0)
      if (types.includes('QUALIFIED'))   out.push(1)
      return out
    },
    canResubmit() {
      // After a rejection the user is allowed to re-upload another
      // SoF / certificate to replace the bad one — that path goes
      // back to the SoF step.
      return this.requestStatus === 'REJECTED'
    },
    statusCardClass() {
      switch (this.requestStatus) {
        case 'APPROVED': return 'qual-card-success'
        case 'REJECTED': return 'qual-card-danger'
        case 'PENDING':  return 'qual-card-warning'
        default:         return 'qual-card-neutral'
      }
    },
    statusIcon() {
      switch (this.requestStatus) {
        case 'APPROVED': return 'check-circle-fill'
        case 'REJECTED': return 'x-circle-fill'
        case 'PENDING':  return 'hourglass-split'
        default:         return 'file-earmark-text'
      }
    },
    statusTitle() {
      switch (this.requestStatus) {
        case 'APPROVED': return 'Заявка одобрена'
        case 'REJECTED': return 'Заявка на статус розничного клиента отклонена'
        case 'PENDING':  return 'Заявка отправлена'
        case 'DRAFT':    return 'Заявка в процессе'
        default:         return 'Статус заявки'
      }
    },
    statusText() {
      switch (this.requestStatus) {
        case 'APPROVED':
          return 'Ваша заявка одобрена. Статус клиента обновлён, лимит инвестирования увеличен.'
        case 'REJECTED':
          return 'К сожалению, ваша заявка отклонена. Ниже указана причина по каждому документу. Вы можете загрузить новые документы и подать заявку повторно.'
        case 'PENDING':
          return 'Уважаемый Клиент! Благодарим за предоставленные документы, в настоящее время они находятся на проверке. Ожидайте обратной связи.'
        case 'DRAFT':
          return 'Заявка ещё не подана: загрузите все необходимые документы и/или пройдите квалификационный тест, чтобы передать её на рассмотрение.'
        default:
          return 'Текущая заявка ещё не подана.'
      }
    },
    stepLabels() {
      return ['Квалификация', 'Источник происхождения средств', 'Статус']
    },
    stepIndex() {
      if (this.step === 'sof') return 1
      if (this.step === 'done') return 2
      // cooldown is still "test" in user's head — keep dot 0 highlighted
      return 0
    },
    progressFillPercent() {
      // Two segments between three nodes — 50% per completed step.
      return Math.max(0, Math.min(100, this.stepIndex * 50))
    },
    questions() {
      if (!this.testData || !Array.isArray(this.testData.questions)) return []
      return this.testData.questions
    },
    currentQuestion() {
      return this.questions[this.currentQuestionIndex] || null
    },
    isLastQuestion() {
      return this.questions.length > 0 && this.currentQuestionIndex === this.questions.length - 1
    },
    canGoNext() {
      if (!this.currentQuestion) return false
      return this.selectedAnswers[this.currentQuestion.questionId] != null
    },
    cooldownMinutes() {
      return Math.max(1, Math.ceil(this.cooldownSeconds / 60))
    },
    cooldownDisplay() {
      const s = Math.max(0, this.cooldownSeconds | 0)
      const m = Math.floor(s / 60)
      const r = s % 60
      return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
    },
  },
  mounted() {
    this.initFromServerState()
  },
  beforeDestroy() {
    this.stopCooldownTicker()
  },
  methods: {
    // --- stepper navigation ---
    canJumpToStep(idx) {
      // "Submitted once" = packet has actually been sent to MLRO at
      // least once (PENDING / REJECTED / APPROVED). DRAFT alone
      // doesn't count — it auto-opens on the first cert/SoF upload,
      // well before the linear flow is finished, so we'd unlock
      // navigation too early.
      const s = this.request && this.request.status
      const submittedOnce = s === 'PENDING' || s === 'REJECTED' || s === 'APPROVED'
      if (!submittedOnce) return idx === this.stepIndex
      return true
    },
    jumpToStep(idx) {
      if (!this.canJumpToStep(idx)) return
      if (idx === 0) {
        this.step = 'test'
        if (this.method === 'cert') this.fetchCertDocs()
      } else if (idx === 1) {
        this.step = 'sof'
      } else if (idx === 2) {
        this.step = 'done'
      }
    },
    // Rejection follow-up: jump the user back to the SoF step so they
    // can upload a new file. A re-uploaded doc opens a fresh DRAFT
    // request automatically on the backend side (active_lock allows
    // it once the previous one is REJECTED).
    goReupload() {
      this.step = 'sof'
      this.fetchSofDocs()
    },
    // "Resubmit" entry point shown on the rejected status screen.
    // Dismiss the local view of the old REJECTED request so the
    // status panel doesn't keep haunting the SoF step; the next
    // upload will open a fresh DRAFT server-side, and on PROFESSIONAL
    // / RETAIL_Q the qualifier already-passed gets carried forward
    // automatically by EnsureActiveRequest.
    resubmitApplication() {
      // Land on the EARLIEST step that still has a problem — i.e. the
      // first red node in the stepper. rejectedStepIndices is already
      // sorted ascending and uses the same "latest doc per type" rule
      // as the banner copy, so navigation and visual cues agree.
      const indices = this.rejectedStepIndices
      const firstRejectedStep = indices.length ? Math.min(...indices) : -1
      const hasCert = Array.isArray(this.certDocs) && this.certDocs.length > 0
      // NOTE: we keep this.request intact. Clearing it would erase
      // requestStatus and break canJumpToStep('Статус') — the user
      // wouldn't be able to navigate back to the status step after
      // jumping away. The request stays REJECTED in the local state
      // until the next upload triggers a backend recompute and we
      // refresh from /ptest/clientstatus.
      if (!this.method) this.method = hasCert ? 'cert' : 'test'
      // Step indices: 0 = "Квалификация" (test or cert), 1 = SoF.
      if (firstRejectedStep === 0) {
        this.method = 'cert'
        this.step = 'test'
      } else if (firstRejectedStep === 1) {
        this.step = 'sof'
        this.fetchSofDocs()
      } else if (this.qualifiedPassed || hasCert) {
        this.step = 'sof'
        this.fetchSofDocs()
      } else {
        this.step = 'test'
      }
    },
    // Russian-language doc-type label for the rejection list.
    docTypeLabel(t) {
      const map = {
        QUALIFIED:        'Источник происхождения средств',
        CERTIFICATE:      'Сертификат (CFA / FRM / CAIA / CIIA)',
        PROFESSIONAL:     'Архив документов',
        PROFESSIONAL_SOF: 'Источник средств (Professional)',
        PROFESSIONAL_CERT:'Сертификат / диплом',
        PROFESSIONAL_HISTORY: 'Торговая история',
      }
      return map[t] || t || 'Документ'
    },

    // --- bootstrap ---
    initFromServerState() {
      // One backend call now: /ptest/clientstatus rolls up
      // clientCategory, isPassedQualified and both document lists so
      // we can decide the starting step from a complete picture in a
      // single round trip.
      this.loading = true
      const headers = getHeader()
      axios.get(`${apiDomain}/ptest/clientstatus`, { headers })
        .then(res => {
          const d = res.data || {}
          this.sofDocs = Array.isArray(d.sofDocs) ? d.sofDocs : []
          this.certDocs = Array.isArray(d.certDocs) ? d.certDocs : []
          this.request = d.request || null
          const cat = d.clientCategory
          if (cat === 'RETAIL_Q' || cat === 'PROFESSIONAL') {
            // Already qualified — wizard isn't relevant; jump to done.
            this.step = 'done'
            return
          }
          this.qualifiedPassed = !!d.isPassedQualified
          const hasCert = this.certDocs.length > 0
          if (d.isPassedQualified || hasCert) {
            // Qualifier is already in hand (test passed or
            // certificate uploaded). The SoF step is the next
            // legitimate stop — the order test/cert -> SoF is
            // enforced both here and on the backend.
            this.method = hasCert ? 'cert' : 'test'
            this.step = 'sof'
            return
          }
          // No qualifier yet. Even if the user somehow has SoF on
          // file (shouldn't happen — the backend rejects it now),
          // we still funnel them through the test/cert picker so
          // the application order stays correct.
          this.step = 'test'
          this.method = null
        })
        .catch(() => {
          // Network/backend hiccup — drop the user on the method
          // picker so they can still pick a path manually.
          this.step = 'test'
          this.method = null
        })
        .finally(() => { this.loading = false })
    },

    // --- step 1: method picker / cert / test ---
    pickMethod(m) {
      // Block re-taking the test once it has been passed — the user
      // should be moving on to SoF, not redoing the exam.
      if (m === 'test' && this.qualifiedPassed) {
        this.step = 'sof'
        return
      }
      this.method = m
      if (m === 'test') {
        this.testPhase = 'questions'
        this.loadTest()
      } else if (m === 'cert') {
        // Pull any previously-submitted certificate so the user sees
        // what's already pending review instead of thinking they need
        // to upload it again.
        this.fetchCertDocs()
      }
    },
    resetMethod() {
      this.method = null
      this.certFiles = []
      this.certError = null
    },
    onCertFileSelect(e) {
      this.addCertFiles(e.target.files)
      e.target.value = ''
    },
    onCertDrop(e) {
      this.certDragOver = false
      this.addCertFiles(e.dataTransfer.files)
    },
    addCertFiles(fileList) {
      this.certError = null
      const allowed = ['.pdf', '.jpg', '.jpeg', '.png', '.zip', '.rar']
      const incoming = Array.from(fileList || [])
      const errors = []
      const accepted = []
      for (const f of incoming) {
        const name = (f.name || '').toLowerCase()
        if (!allowed.some(ext => name.endsWith(ext))) {
          errors.push(`${f.name}: разрешены PDF, JPG, PNG, ZIP, RAR`)
          continue
        }
        if (f.size > MAX_FILE_SIZE) {
          errors.push(`${f.name}: больше 10 МБ`)
          continue
        }
        // Skip exact duplicates already in the queue (name+size is good
        // enough — user mistakenly dragging the same file twice).
        const dup = this.certFiles.some(x => x.name === f.name && x.size === f.size)
        if (!dup) accepted.push(f)
      }
      if (accepted.length) this.certFiles = this.certFiles.concat(accepted)
      if (errors.length) this.certError = errors.join('; ')
    },
    removeCertFile(idx) {
      this.certFiles.splice(idx, 1)
      this.certError = null
    },
    clearCertFiles() {
      this.certFiles = []
      this.certError = null
      if (this.$refs.certInput) this.$refs.certInput.value = ''
    },
    uploadCertOnly() {
      // Standalone certificate upload — used when the user already
      // submitted SoF and now wants to add another certificate.
      // Backend endpoint /ptest/postcert writes one CERTIFICATE row
      // per attached file without touching SoF.
      if (!this.certFiles.length || this.certUploading) return
      this.certUploadError = null
      this.certUploading = true
      const form = new FormData()
      this.certFiles.forEach(f => form.append('certDoc', f))
      const baseHeaders = getHeader()
      const headers = baseHeaders ? { Authorization: baseHeaders.Authorization } : {}
      axios.post(`${apiDomain}/ptest/postcert`, form, { headers })
        .then(res => {
          const ok = res.status === 200 && (
            res.data === 'success' ||
            (res.data && (res.data.success === true || res.data.status === 'success'))
          )
          if (!ok) {
            this.certUploadError = (res.data && (res.data.message || res.data.msg)) || 'Загрузка не удалась.'
            return
          }
          this.clearCertFiles()
          this.fetchCertDocs()
          this.refreshRequest()
          if (this.$bvToast) {
            this.$bvToast.toast('Сертификат отправлен на проверку.', {
              title: 'Готово', variant: 'success', solid: true,
              toaster: 'b-toaster-top-center', autoHideDelay: 3500,
            })
          }
        })
        .catch(err => {
          this.certUploadError = (err.response && err.response.data && (err.response.data.message || err.response.data.msg))
            || err.message || 'Не удалось загрузить сертификат.'
        })
        .finally(() => { this.certUploading = false })
    },
    goBackToCert() {
      // Return from the SoF step to the certificate step so the user
      // can upload another cert without re-submitting SoF. Step 1
      // already lists everything they've sent.
      this.step = 'test'
      this.testPhase = 'questions'
      this.method = 'cert'
      this.fetchCertDocs()
    },
    advanceFromCert() {
      // Single "Далее" on the cert step: if new files are picked we
      // upload them via /ptest/postcert first (one CERTIFICATE row per
      // file), otherwise we just move on with whatever the user already
      // has on file. The clickable stepper lets them come back to drop
      // another cert later.
      if (this.certFiles.length) {
        this.certUploading = true
        this.certUploadError = null
        const form = new FormData()
        this.certFiles.forEach(f => form.append('certDoc', f))
        const baseHeaders = getHeader()
        const headers = baseHeaders ? { Authorization: baseHeaders.Authorization } : {}
        axios.post(`${apiDomain}/ptest/postcert`, form, { headers })
          .then(res => {
            const ok = res.status === 200 && (
              res.data === 'success' ||
              (res.data && (res.data.success === true || res.data.status === 'success'))
            )
            if (!ok) {
              this.certUploadError = (res.data && (res.data.message || res.data.msg)) || 'Загрузка не удалась.'
              return
            }
            this.clearCertFiles()
            this.fetchCertDocs()
            this.refreshRequest()
            this.step = 'sof'
          })
          .catch(err => {
            this.certUploadError = (err.response && err.response.data && (err.response.data.message || err.response.data.msg))
              || err.message || 'Не удалось загрузить сертификат.'
          })
          .finally(() => { this.certUploading = false })
      } else if (this.certDocs.length) {
        this.step = 'sof'
      }
    },

    // --- test sub-flow ---
    loadTest() {
      this.loading = true
      axios.get(`${apiDomain}/ptest/qualified`, { headers: getHeader() })
        .then(res => {
          const raw = res.data
          const data = (raw && Array.isArray(raw.questions))
            ? { ...raw, questions: this.shuffleArray(raw.questions).map(q => ({ ...q, answers: this.shuffleArray(q.answers || []) })) }
            : raw
          this.testData = data
          this.currentQuestionIndex = 0
          this.selectedAnswers = {}
          this.testPhase = 'questions'
          this.step = 'test'
        })
        .catch(err => {
          const resp = err.response
          if (resp && resp.status === 429 && resp.data && resp.data.error === 'qualified-test-cooldown') {
            this.cooldownMessage = resp.data.message || ''
            this.cooldownSeconds = Number(resp.data.retryAfterSeconds) || 0
            this.step = 'cooldown'
            this.startCooldownTicker()
          } else {
            this.toast('Не удалось загрузить тест. Попробуйте позже.', 'danger')
          }
        })
        .finally(() => { this.loading = false })
    },
    shuffleArray(arr) {
      const a = arr.slice()
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]]
      }
      return a
    },
    selectAnswer(qid, a) {
      this.$set(this.selectedAnswers, qid, a)
    },
    isAnswerSelected(qid, aid) {
      const sel = this.selectedAnswers[qid]
      return sel && sel.answerId === aid
    },
    nextQuestion() {
      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex++
      }
    },
    prevQuestion() {
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--
      }
    },
    submitTest() {
      const total = this.questions.length
      const correct = this.questions.filter(
        q => this.selectedAnswers[q.questionId] && this.selectedAnswers[q.questionId].isCorrect === 1
      ).length
      const uid = this.loginedUser && (this.loginedUser.id || this.loginedUser.userId)
      // Audit-trail payload: one entry per rendered question with the
      // answer the user actually picked. Backend persists these in
      // p_test_answer_response so MLRO can review the responses later.
      const answers = this.questions
        .filter(q => this.selectedAnswers[q.questionId])
        .map(q => ({
          questionId: Number(q.questionId),
          answerId: Number(this.selectedAnswers[q.questionId].answerId),
          isCorrect: this.selectedAnswers[q.questionId].isCorrect === 1 ? 1 : 0,
        }))
      this.submitting = true
      axios.post(
        `${apiDomain}/ptest/postbasic`,
        { testCode: 'QUALIFIED', totalQuestionCount: total, correctAnswerCount: correct, userId: Number(uid), answers },
        { headers: getHeader() }
      )
      .then(res => {
        const ok = res.data === 'success' || (res.data && (res.data.status === 'success' || res.data.success === true))
        if (ok) {
          EventBus.$emit('refresh-user-details')
          this.step = 'sof'
        } else {
          // failed
          if (res.data && typeof res.data.attemptsLeft === 'number') {
            this.attemptsLeft = res.data.attemptsLeft
          }
          this.testPhase = 'failed'
        }
      })
      .catch(() => {
        this.toast('Не удалось отправить результат теста. Попробуйте ещё раз.', 'danger')
      })
      .finally(() => { this.submitting = false })
    },
    restartTest() {
      // Reset state and pull a fresh question set (random order). If the
      // 3-strikes cooldown just kicked in, loadTest will switch to that
      // screen instead.
      this.attemptsLeft = null
      this.loadTest()
    },
    startCooldownTicker() {
      this.stopCooldownTicker()
      if (this.cooldownSeconds <= 0) return
      this.cooldownTimer = setInterval(() => {
        this.cooldownSeconds = Math.max(0, this.cooldownSeconds - 1)
        if (this.cooldownSeconds === 0) {
          this.stopCooldownTicker()
          // Cool-down elapsed — reload server state so the user lands on
          // the method picker (or wherever clientstatus dictates).
          this.initFromServerState()
        }
      }, 1000)
    },
    stopCooldownTicker() {
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer)
        this.cooldownTimer = null
      }
    },

    // --- step 2: SoF upload ---
    onSofFileSelect(e) {
      const f = e.target.files && e.target.files[0]
      if (f) this.setSofFile(f)
      e.target.value = ''
    },
    onSofDrop(e) {
      this.sofDragOver = false
      const f = e.dataTransfer.files && e.dataTransfer.files[0]
      if (f) this.setSofFile(f)
    },
    setSofFile(f) {
      this.sofError = null
      const name = (f.name || '').toLowerCase()
      const allowed = ['.pdf', '.jpg', '.jpeg', '.png', '.zip', '.rar']
      if (!allowed.some(ext => name.endsWith(ext))) {
        this.sofError = 'Разрешены PDF, JPG, PNG, ZIP или RAR.'
        return
      }
      if (f.size > MAX_FILE_SIZE) {
        this.sofError = 'Файл не должен превышать 10 МБ.'
        return
      }
      this.sofFile = f
    },
    clearSofFile() {
      this.sofFile = null
      this.sofError = null
      if (this.$refs.sofInput) this.$refs.sofInput.value = ''
    },
    uploadSof() {
      if (!this.sofFile || this.uploading) return
      // Cert path acceptance: either a fresh cert is queued for this
      // submission (sent together as a multipart) OR the user has
      // already sent at least one cert via /ptest/postcert (which is
      // why advanceFromCert and the standalone upload exist). Only if
      // both are empty do we block — that means the user truly hasn't
      // attached anything.
      if (this.method === 'cert' && !this.certFile && this.certDocs.length === 0) {
        this.sofError = 'Сертификат не приложен — вернитесь на шаг 1.'
        return
      }
      this.sofError = null
      this.uploading = true
      const form = new FormData()
      form.append('docType', 'QUALIFIED')
      if (this.method === 'cert' && this.certFile) {
        form.append('certDoc', this.certFile)
      }
      form.append('clientDoc', this.sofFile)
      const baseHeaders = getHeader()
      const headers = baseHeaders ? { Authorization: baseHeaders.Authorization } : {}
      axios.post(`${apiDomain}/ptest/postclientfile`, form, { headers })
        .then(res => {
          const ok = res.status === 200 && (
            res.data === 'success' ||
            (res.data && (res.data.success === true || res.data.status === 'success'))
          )
          if (!ok) {
            this.sofError = (res.data && (res.data.message || res.data.msg)) || 'Загрузка не удалась.'
            return
          }
          EventBus.$emit('refresh-user-details')
          this.fetchSofDocs()
          // Drop the just-uploaded files from the local picker so a
          // round-trip back to the SoF step doesn't re-send them when
          // the user clicks "Отправить" without choosing anything new.
          this.sofFile = null
          if (this.method === 'cert') this.certFile = null
          // Pull the freshly-opened (or just-moved-to-PENDING)
          // classification request so the status screen renders the
          // real state instead of the cached "ещё не подана" copy.
          this.refreshRequest()
          this.step = 'done'
        })
        .catch(err => {
          this.sofError = (err.response && err.response.data && (err.response.data.message || err.response.data.msg))
            || err.message || 'Не удалось загрузить файл. Попробуйте позже.'
        })
        .finally(() => { this.uploading = false })
    },
    // Re-fetch just the classification request (no other state) so
    // the status screen reflects what happened after the user
    // submitted SoF / certificate / test. Called after every action
    // that may have created or moved the request.
    refreshRequest() {
      const userId = this.loginedUser && this.loginedUser.id
      if (!userId) return
      axios.get(`${apiDomain}/ptest/clientstatus`, { headers: getHeader() })
        .then(res => {
          const d = res.data || {}
          this.request = d.request || null
        })
        .catch(() => { /* keep the existing value silently */ })
    },
    leaveForLater() {
      // The toast in Landing + the profile card will keep nudging.
      this.$router.push('/landing').catch(() => {})
    },

    // --- already-uploaded docs ---
    fetchDocs(docType) {
      const headers = getHeader()
      if (!headers) return Promise.resolve([])
      return axios.get(`${apiDomain}/ptest/clientdoclist/${docType}`, { headers })
        .then(res => {
          const data = res.data
          return Array.isArray(data) ? data : (data && (data.list || data.data)) || []
        })
        .catch(() => [])
    },
    fetchSofDocs() {
      this.docListLoading = true
      this.fetchDocs('QUALIFIED')
        .then(list => { this.sofDocs = list })
        .finally(() => { this.docListLoading = false })
    },
    fetchCertDocs() {
      this.docListLoading = true
      this.fetchDocs('CERTIFICATE')
        .then(list => { this.certDocs = list })
        .finally(() => { this.docListLoading = false })
    },
    docFileName(path) {
      if (!path) return ''
      const parts = String(path).split(/[\\/]/)
      return parts[parts.length - 1] || ''
    },
    downloadDoc(doc) {
      const fileName = this.docFileName(doc && doc.docPath)
      if (!fileName) return
      const url = `${apiDomain}/ptest/downloadclientfile/${encodeURIComponent(fileName)}`
      axios.get(url, { headers: getHeader(), responseType: 'blob' })
        .then(response => {
          const blob = new Blob([response.data], { type: response.data.type || 'application/octet-stream' })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = fileName
          link.click()
          window.URL.revokeObjectURL(link.href)
        })
        .catch(() => this.toast('Не удалось скачать файл.', 'danger'))
    },

    toast(msg, variant) {
      if (!this.$bvToast) return
      this.$bvToast.toast(msg, {
        title: variant === 'danger' ? 'Ошибка' : 'Готово',
        variant,
        autoHideDelay: 5000,
        toaster: 'b-toaster-top-center',
        solid: true,
      })
    },
  },
}
</script>

<style scoped>
.qual-flow {
  min-height: 70vh;
  padding: 32px 24px 64px;
  background: linear-gradient(180deg, #0a0a0f 0%, #14141a 100%);
  color: #f8fafc;
}
.qual-flow-container {
  max-width: 760px;
  margin: 0 auto;
}

/* ===== Stepper ===== */
.qual-stepper {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin-bottom: 36px;
}
.qual-stepper-line {
  position: absolute;
  top: 17px;
  left: 16.66%;
  right: 16.66%;
  height: 2px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 2px;
}
.qual-stepper-fill {
  height: 100%;
  background: #EFB914;
  border-radius: 2px;
  transition: width 0.3s ease;
}
.qual-stepper-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  background: transparent;
  border: none;
  padding: 0;
  outline: none;
  cursor: default;
}
.qual-stepper-node.is-clickable {
  cursor: pointer;
}
.qual-stepper-node:disabled {
  cursor: not-allowed;
}
.qual-stepper-node.is-clickable:hover .qual-stepper-dot {
  border-color: rgba(239, 185, 20, 0.7);
}
.qual-stepper-dot {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(20, 22, 30, 0.95);
  border: 2px solid rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 14px;
}
.qual-stepper-node.is-done .qual-stepper-dot {
  background: #EFB914;
  border-color: #EFB914;
  color: #18191D;
}
.qual-stepper-node.is-current .qual-stepper-dot {
  border-color: #EFB914;
  color: #EFB914;
  box-shadow: 0 0 0 4px rgba(239, 185, 20, 0.18);
}
.qual-stepper-label {
  margin-top: 8px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.65);
  font-weight: 600;
}
.qual-stepper-node.is-current .qual-stepper-label {
  color: #f8fafc;
}
.qual-stepper-node.is-rejected .qual-stepper-dot {
  background: #EF4444;
  border-color: #EF4444;
  color: #fff;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.22);
}
.qual-stepper-node.is-rejected .qual-stepper-label {
  color: #FCA5A5;
}

/* ===== Cards ===== */
.qual-card {
  border-radius: 20px;
  padding: 28px;
  border: 1px solid rgba(255, 255, 255, 0.10);
  background: linear-gradient(145deg, rgba(37, 40, 64, 0.5), rgba(20, 22, 35, 0.75));
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.3);
}
.qual-card-warning {
  border-color: rgba(239, 185, 20, 0.45);
  background: linear-gradient(145deg, rgba(239, 185, 20, 0.10), rgba(20, 22, 35, 0.75));
  text-align: center;
}
.qual-card-success {
  border-color: rgba(76, 175, 80, 0.45);
  background: linear-gradient(145deg, rgba(76, 175, 80, 0.10), rgba(20, 22, 35, 0.75));
  text-align: center;
}
.qual-card-danger {
  border-color: rgba(239, 68, 68, 0.5);
  background: linear-gradient(145deg, rgba(239, 68, 68, 0.10), rgba(20, 22, 35, 0.75));
  text-align: center;
}
.qual-card-neutral {
  border-color: rgba(255, 255, 255, 0.12);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.04), rgba(20, 22, 35, 0.75));
  text-align: center;
}
.qual-card-icon {
  font-size: 2.5rem;
  color: #EFB914;
  margin-bottom: 12px;
}
.qual-card-success .qual-card-icon { color: #66BB6A; }
.qual-card-danger  .qual-card-icon { color: #F87171; }
.qual-card-neutral .qual-card-icon { color: rgba(255, 255, 255, 0.55); }

/* Rejected-doc rationale list shown on the status card after REJECTED. */
.qual-rejected-list {
  margin: 14px 0 16px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.qual-rejected-row {
  background: rgba(20, 22, 35, 0.55);
  border: 1px solid rgba(239, 68, 68, 0.28);
  border-radius: 10px;
  padding: 10px 14px;
}
.qual-rejected-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 4px;
}
.qual-rejected-name { font-weight: 700; color: rgba(255, 255, 255, 0.95); font-size: 14px; }
.qual-rejected-badge {
  background: rgba(239, 68, 68, 0.18);
  color: #FCA5A5;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
}
.qual-rejected-reason {
  color: rgba(255, 255, 255, 0.78);
  font-size: 13px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 7px;
  padding: 8px 10px;
}
.qual-rejected-reason strong { color: #FCA5A5; font-weight: 700; }
.qual-card-title {
  font-size: 1.3rem;
  font-weight: 800;
  margin-bottom: 12px;
}
.qual-card-text {
  color: rgba(255, 255, 255, 0.82);
  font-size: 14px;
  line-height: 1.55;
  margin-bottom: 12px;
}
.qual-card-hint {
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  margin-bottom: 16px;
}

.qual-loading {
  text-align: center;
  padding: 60px 0;
  color: rgba(255, 255, 255, 0.7);
}

/* Test */
.qual-progress-text {
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
  margin-bottom: 18px;
}
.qual-question-text {
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 18px;
  line-height: 1.4;
}
.qual-answers {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}
.qual-answer-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  cursor: pointer;
  transition: 150ms ease;
}
.qual-answer-label:hover {
  border-color: rgba(239, 185, 20, 0.55);
}
.qual-answer-label.checked {
  border-color: #EFB914;
  background: rgba(239, 185, 20, 0.12);
}
.qual-answer-label input[type="radio"] {
  margin-top: 3px;
  accent-color: #EFB914;
}
.qual-answer-text {
  flex: 1;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.92);
}

/* Upload */
.qual-upload-zone {
  position: relative;
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 32px 20px;
  text-align: center;
  cursor: pointer;
  transition: 150ms ease;
  margin-top: 18px;
  margin-bottom: 18px;
}
.qual-upload-zone:hover,
.qual-upload-zone.is-dragover {
  border-color: #EFB914;
  background: rgba(239, 185, 20, 0.05);
}
.qual-upload-zone.has-file {
  border-style: solid;
  border-color: rgba(239, 185, 20, 0.55);
  background: rgba(239, 185, 20, 0.06);
}
.qual-upload-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  display: none;
}
.qual-upload-icon {
  font-size: 1.8rem;
  color: #EFB914;
  margin-bottom: 8px;
}
.qual-upload-text {
  font-weight: 700;
  margin-bottom: 4px;
}
.qual-upload-hint {
  color: rgba(255, 255, 255, 0.55);
  font-size: 12px;
}
.qual-upload-filename {
  font-weight: 700;
  font-family: monospace;
  margin-bottom: 8px;
  word-break: break-all;
}
.qual-upload-clear {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.85);
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12px;
  cursor: pointer;
}

.qual-upload-files {
  list-style: none;
  padding: 0;
  margin: 12px 0 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.qual-upload-files-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  font-size: 13px;
}
.qual-upload-files-name {
  flex: 1;
  word-break: break-all;
  font-family: monospace;
}
.qual-upload-files-remove {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  border-radius: 6px;
}
.qual-upload-files-remove:hover {
  color: #f87171;
  background: rgba(248, 113, 113, 0.1);
}

.qual-error {
  color: #f87171;
  font-size: 13px;
  margin-bottom: 8px;
}

.qual-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}
.qual-card-warning .qual-actions,
.qual-card-success .qual-actions,
.qual-card-danger  .qual-actions,
.qual-card-neutral .qual-actions { justify-content: center; gap: 10px; flex-wrap: wrap; }

/* ===== Method picker cards (step 1) ===== */
.qual-method-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
}
.qual-method-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  padding: 22px;
  border-radius: 18px;
  border: 2px solid rgba(255, 255, 255, 0.10);
  background: rgba(255, 255, 255, 0.04);
  color: #f8fafc;
  cursor: pointer;
  transition: 150ms ease;
}
.qual-method-card:hover {
  border-color: rgba(239, 185, 20, 0.55);
  background: rgba(239, 185, 20, 0.06);
  transform: translateY(-2px);
}
.qual-method-card.is-done {
  border-color: rgba(74, 222, 128, 0.45);
  background: rgba(74, 222, 128, 0.05);
  cursor: default;
  opacity: 0.85;
}
.qual-method-card.is-done:hover {
  transform: none;
}
.qual-method-card.is-done .qual-method-icon { color: #4ade80; }
.qual-method-icon {
  font-size: 1.8rem;
  color: #EFB914;
  margin-bottom: 10px;
}
.qual-method-title {
  font-weight: 800;
  font-size: 1rem;
  margin-bottom: 6px;
}
.qual-method-sub {
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  line-height: 1.5;
}

/* ===== Uploaded-docs table on the SoF step ===== */
.qual-doc-list {
  margin-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.10);
  padding-top: 18px;
}
.qual-doc-list-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.qual-doc-list-title {
  color: rgba(255, 255, 255, 0.85);
  font-weight: 700;
  margin: 0;
}
.qual-doc-list-empty {
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
  padding: 12px 4px;
}
.qual-doc-list-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.qual-doc-list-table th {
  text-align: left;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.55);
  padding: 8px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
}
.qual-doc-list-table td {
  padding: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  vertical-align: top;
}
.qual-doc-type {
  font-weight: 700;
  color: rgba(255, 255, 255, 0.92);
}
.qual-doc-comment {
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
}
.qual-doc-status {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.8);
}
.qual-doc-status-pending  { background: rgba(239, 185, 20, 0.18); color: #EFB914; }
.qual-doc-status-approved { background: rgba(76, 175, 80, 0.18);  color: #66BB6A; }
.qual-doc-status-rejected { background: rgba(239, 83, 80, 0.18);  color: #EF5350; }
</style>
